import MenuItem from '@mui/material/MenuItem';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import { type ForwardedRef, forwardRef } from 'react';

import { Text } from '../../text';
import type { ItemComponentProps, OptionSelectionProps } from '../types';

type SelectionProps = Omit<
  TextFieldProps,
  'type' | 'multiline' | 'minRows' | 'maxRows' | 'select'
> & {
  options: OptionSelectionProps[];
  itemComponent?: React.FC<ItemComponentProps>;
};

interface DefaultItemProps {
  option: OptionSelectionProps;
}

function DefaultItem(props: Readonly<DefaultItemProps>) {
  const { option } = props;

  if (typeof option.label === 'string') return <Text>{option.label}</Text>;
  return option.label;
}

const Selection = forwardRef(
  (props: Readonly<SelectionProps>, ref: ForwardedRef<HTMLDivElement>) => {
    const {
      placeholder,
      options,
      itemComponent: ItemComponent = DefaultItem,
      ...restProps
    } = props;

    const renderValue = (option: OptionSelectionProps) => {
      if (typeof option.label === 'string') return <Text>{option.label}</Text>;
      return option.label;
    };

    return (
      <TextField
        ref={ref}
        slotProps={{
          select: {
            renderValue: (value) => {
              const item = options.find(({ value: v }) => v === value);
              if (!item) return <Text color='grey.500'>{placeholder}</Text>;
              return renderValue(item);
            },
          },
        }}
        {...restProps}
        select
      >
        {Array.isArray(options)
          ? options.map((option, index) => (
              <MenuItem key={`${index}-${option.value}`} value={option.value}>
                <ItemComponent index={index} option={option} />
              </MenuItem>
            ))
          : null}
      </TextField>
    );
  },
);

Selection.displayName = 'Selection';

export type { SelectionProps, OptionSelectionProps };
export { Selection };
